import React, { useEffect, useState } from 'react';
import { enforceMoneyFormat } from '../../utils/formUtils';

const assetFieldIds = [
    { bank: "DD0102", accNo: "DD0110", balance: "DD0148" },
    { bank: "DD0202", accNo: "DD0210", balance: "DD0248" },
    { bank: "DD0302", accNo: "DD0310", balance: "DD0348" },
    { bank: "DD0402", accNo: "DD0410", balance: "DD0448" },
    { bank: "DD0502", accNo: "DD0510", balance: "DD0548" },
    { bank: "DD0602", accNo: "DD0610", balance: "DD0648" },
    { bank: 'DD0702', accNo: 'DD0710', balance: 'DD0748' },
    { bank: 'DD0802', accNo: 'DD0810', balance: 'DD0848' },
];

const housingRows = [
    { label: "Rent", present: "119", proposed: null },
    { label: "First Mortgage (P&I)", present: "120", proposed: "228" },
    { label: "Other Financing (P&I)", present: "121", proposed: "229" },
    { label: "Hazard Insurance", present: "122", proposed: "230" },
    { label: "Real Estate Taxes", present: "123", proposed: "1405" },
    { label: "Homeowner Assn. Dues", present: "125", proposed: "233" },
    { label: "Other", present: "126", proposed: "234" },
    { label: "Total", present: "1299", proposed: "912" }
];

const AssetInformation = ({ fieldValues, vodList, setVodList }) => {
    const [otherLabel, setOtherLabel] = useState(fieldValues["CX.BPL.HOUSING.OTHER.NAME"] || "");
    const [presentValues, setPresentValues] = useState({});
    const [proposedValues, setProposedValues] = useState({});

    useEffect(() => {
        if (fieldValues["CX.BPL.HOUSING.OTHER.NAME"]) {
            setOtherLabel(fieldValues["CX.BPL.HOUSING.OTHER.NAME"]);
        }
    }, [fieldValues]);

    const calculateTotal = (values, ids) => {
        return ids.reduce((sum, id) => {
            const val = parseFloat((values[id] || "0").replace(/,/g, ""));
            return sum + (isNaN(val) ? 0 : val);
        }, 0).toFixed(2);
    };

    useEffect(() => {
        const presetInit = {};
        const proposedInit = {};
        housingRows.forEach(({ present, proposed }) => {
            if (present) presetInit[present] = fieldValues[present] || '';
            if (proposed) proposedInit[proposed] = fieldValues[proposed] || '';
        });
        setPresentValues(presetInit);
        setProposedValues(proposedInit);
    }, [fieldValues]);

    const handlePresentChange = (e, id) => {
        const val = e.target.value;
        setPresentValues(prev => ({
            ...prev,
            [id]: val
        }));
    };

    const handleProposedChange = (e, id) => {
        const val = e.target.value;
        setProposedValues(prev => ({
            ...prev,
            [id]: val
        }));
    };

    const presentSum = calculateTotal(presentValues, housingRows.filter(r => r.present && r.label !== "Total").map(r => r.present));
    const proposedSum = calculateTotal(proposedValues, housingRows.filter(r => r.proposed && r.label !== "Total").map(r => r.proposed));

    useEffect(() => {
        const filledRows = [...vodList];
        while (filledRows.length < 8) {
            filledRows.push({
                holderName: '',
                items: [{ itemNumber: 1, accountIdentifier: '', urla2020CashOrMarketValueAmount: '' }],
                isExisting: false,
                isNew: false
            });
        }
        setVodList(filledRows.slice(0, 8)); // Ensure exactly 8 rows
    }, []);

    const handleChange = (index, field, value) => {
        const updated = [...vodList];
        updated[index][field] = value;
        if (!updated[index].isExisting && (value || updated[index].items[0]?.accountIdentifier || updated[index].items[0]?.urla2020CashOrMarketValueAmount)) {
            updated[index].isNew = true;
        }
        setVodList(updated);
    };

    const handleItemChange = (index, itemField, value) => {
        const updated = [...vodList];
        if (!updated[index].items) {
            updated[index].items = [{ itemNumber: 1 }];
        }
        updated[index].items[0][itemField] = value;
        if (!updated[index].isExisting && (updated[index].holderName || value)) {
            updated[index].isNew = true;
        }
        setVodList(updated);
    };

    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
            <div className="section-header">
                Asset Information & Housing Information
            </div>

            {/* Asset Information Rows */}
            <div className="px-4 py-4">
                <div className="form-grid-3 mb-4">
                    <label className="form-label">Bank Name:</label>
                    <label className="form-label">Account Number:</label>
                    <label className="form-label">Account Balance:</label>
                </div>

                {vodList.map((vod, index) => {
                    const fieldMap = assetFieldIds[index]; // Only defined for first 8
                    const item = vod.items?.[0] || {};

                    return (
                        <div className="form-grid-3 mb-2" key={`vod-${index}`}>
                            {/* Bank Name */}
                            <input
                                type="text"
                                className="form-input"
                                id={`field-${fieldMap.bank}`}
                                defaultValue={vod.isExisting ? fieldValues?.[fieldMap.bank] || '' : vod.holderName}
                                value={!vod.isExisting ? vod.holderName : undefined}
                                onChange={(e) => handleChange(index, 'holderName', e.target.value)}
                            />

                            {/* Account Number */}
                            <input
                                type="text"
                                className="form-input"
                                id={`field-${fieldMap.accNo}`}
                                defaultValue={vod.isExisting ? fieldValues?.[fieldMap.accNo] || '' : item.accountIdentifier}
                                value={!vod.isExisting ? item.accountIdentifier : undefined}
                                onChange={(e) => handleItemChange(index, 'accountIdentifier', e.target.value)}
                            />

                            {/* Account Balance */}
                            <input
                                type="text"
                                className="form-input"
                                id={`field-${fieldMap.balance}`}
                                defaultValue={vod.isExisting ? fieldValues?.[fieldMap.balance] || '' : item.urla2020CashOrMarketValueAmount}
                                value={!vod.isExisting ? item.urla2020CashOrMarketValueAmount : undefined}
                                onChange={(e) => handleItemChange(index, 'urla2020CashOrMarketValueAmount', e.target.value)}
                                onBlur={enforceMoneyFormat}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Housing Information */}
            <div className="px-4 pb-6">
                <div className="form-grid-3 mb-4">
                    <label className="form-label col-span-3"></label>
                    <span className="text-sm form-label">Combined Monthly Housing Expense:</span>
                    <label className="text-sm form-label">Present:</label>
                    <label className="text-sm form-label">Proposed:</label>
                </div>

                {housingRows.map(({ label, present, proposed }, idx) => {
                    const isTotalRow = label === "Total";

                    return (
                        <div className="form-grid-3 mb-2 items-center" key={idx}>
                            <div className="checkbox-label">
                                <label className="text-sm text-[var(--slate-grey-2)]">{label}</label>
                                {label === "Other" && (
                                    <input
                                        type="text"
                                        placeholder="Type expense"
                                        id="field-CX.BPL.HOUSING.OTHER.NAME"
                                        value={otherLabel}
                                        onChange={(e) => setOtherLabel(e.target.value)}
                                        className="form-input w-48 mt-1"
                                    />
                                )}
                            </div>

                            <input
                                type="text"
                                className="form-input"
                                id={`field-${present}`}
                                value={isTotalRow ? presentSum : presentValues[present] || ""}
                                onChange={(e) => handlePresentChange(e, present)}
                                readOnly={isTotalRow}
                                onBlur={enforceMoneyFormat}
                            />

                            {label !== "Rent" ? (
                                <input
                                    type="text"
                                    className="form-input"
                                    id={`field-${proposed}`}
                                    value={isTotalRow ? proposedSum : proposedValues[proposed] || ""}
                                    onChange={(e) => handleProposedChange(e, proposed)}
                                    readOnly={isTotalRow}
                                    onBlur={enforceMoneyFormat}
                                />
                            ) : (
                                <div></div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AssetInformation;
